import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from 'primereact/inputtextarea';
import { Paginator } from "primereact/paginator";
import { ProgressBar } from 'primereact/progressbar';
import { Rating } from 'primereact/rating';
import { Toast } from 'primereact/toast';
import React, { useRef, useState } from "react";
import { axiosInstance } from '../axios/axiosConfig';
import { getCurrentUserFromLocalStorage } from '../helpers/localStorageHelper';
import { createRatingForTrainer, getRatingDetailsByTrainerId } from '../services/trainersAPIService';
import { convertUTCtoLocalTime } from "../helpers/dateTimeHelper";
import { modifyMediaUrl } from '../helpers/modifyMediaUrl';
import { translate } from '../localization/translateService';
import { Avatar } from "primereact/avatar";
import { WarnTemplate } from "./common/ToastMessage/Template";
import LoadingButton from "./common/LoadingButton";

const defaultState = {
    selectedRatingStar: 0,
    ratingList: [],
    ratingPercent: [
        { ratingStar: 5, percent: 0 },
        { ratingStar: 4, percent: 0 },
        { ratingStar: 3, percent: 0 },
        { ratingStar: 2, percent: 0 },
        { ratingStar: 1, percent: 0 }
    ],
    ratingValue: 0,
    comment: "",
    canRating: true,
}

export default function TrainerRating({ props, onRatingSuccess }) {
    const [state, setState] = useState(defaultState);
    const toast = useRef(null);
    const [rows, setRows] = useState(2);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecord, setTotalRecord] = useState(0);
    const [first, setFirst] = useState(0);
    const [isSending, setIsSending] = useState(false);
    const [isAddingRating, setIsAddingRating] = useState(false);
    const currentUser = getCurrentUserFromLocalStorage();
    const [translations, setTranslations] = useState({
        ratingDropdownTitle: {
            oneStart: "",
            twoStart: "",
            threeStart: "",
            fourStart: "",
            fiveStart: "",
            all: ""
        },
        ratingValidateMessage: "",
        ratingTime: {
            justNow: "",
            secondsAgo: "",
            minutesAgo: "",
            hoursAgo: "",
            daysAgo: "",
            weeksAgo: "",
            monthsAgo: "",
            yearsAgo: "",
        },
        ratingPaginatorButton: {
            previous: "",
            next: ""
        },
        ratingHeaderContent: {
            header: "",
            headerContent: "",
            headerRatingContent: "",
            headerRatingContentPlaceholder: "",
        },
        ratingContent: {
            header: "",
            star: ""
        },
        noReviewMessage: "",
        btnSend: ""
    });
    const handleRatingStarChange = (e) => {
        setState((draft) => ({
            ...draft,
            selectedRatingStar: e.value,
        }));
        setPage(1);
        setFirst(0);
    };

    const onRatingValueChange = (e) => {
        setState((draft) => ({
            ...draft,
            ratingValue: e.value,
        }));
    }

    const onCommentChange = (e) => {
        setState((draft) => ({
            ...draft,
            comment: e.target.value,
        }));
    }

    const onPageChange = (event) => {
        setPage(event.page + 1);
        setFirst(event.first);
    };

    // API functions
    const handleFilterRequest = () => {
        const filters = {};
        state.selectedRatingStar !== 0 && (filters.rating = { value: state.selectedRatingStar });
        return filters;
    };

    async function fetchRatingDetails() {
        try {
            const filterRequest = handleFilterRequest();
            const body = {
                first: first,
                rows: rows,
                filters: filterRequest,
            };

            const response = await getRatingDetailsByTrainerId(body, props.trainerId);
            setState((draft) => ({
                ...draft,
                ratingList: response.data.data,
            }));
            setTotalPages(Math.ceil(response.data.pagingInfo.totalRecordCount / rows))
            setTotalRecord(response.data.pagingInfo.totalRecordCount)
        } catch (error) {
        }
    }

    async function createRating(body) {
        try {
            setIsAddingRating(true);
            const response = await createRatingForTrainer(body, props.trainerId)
            const currentRatings = state.ratingList || [];
            const newRatingList = [response.data, ...currentRatings];

            setState((draft) => ({
                ...draft,
                ratingList: newRatingList,
                ratingValue: 0,
                comment: "",
                canRating: false,
            }));
            setPage(1);
            setFirst(0);
            setTotalRecord(prev => {
                const newTotal = prev + 1;
                setTotalPages(Math.ceil(newTotal / rows));
                return newTotal;
            });
            updateRatingPercentFromList(newRatingList, totalRecord + 1);
            const newAverageRating = calculateAverageRating(newRatingList);
            const newNumberRating = totalRecord + 1;
            if (onRatingSuccess) {
                onRatingSuccess(newNumberRating, newAverageRating);
            }
        } catch (error) {
        } finally {
            setTimeout(() => {
                setIsAddingRating(false);
            }, 500);
        }
    }

    async function canUserRatingForTrainerAsync() {
        try {
            const response = await axiosInstance.get(`/trainers/${props.trainerId}/can-rating`);
            setState((draft) => ({
                ...draft,
                canRating: response.data,
            }));
        } catch (error) {
        }
    }

    async function fetchRatingPercent() {
        try {
            const response = await axiosInstance.get(`/trainers/${props.trainerId}/rating/percent`);
            const ratingPercent = [
                { ratingStar: 5, percent: Math.round((response.data.find(d => d.ratingStar === 5)?.percent || 0) * 100) },
                { ratingStar: 4, percent: Math.round((response.data.find(d => d.ratingStar === 4)?.percent || 0) * 100) },
                { ratingStar: 3, percent: Math.round((response.data.find(d => d.ratingStar === 3)?.percent || 0) * 100) },
                { ratingStar: 2, percent: Math.round((response.data.find(d => d.ratingStar === 2)?.percent || 0) * 100) },
                { ratingStar: 1, percent: Math.round((response.data.find(d => d.ratingStar === 1)?.percent || 0) * 100) }
            ]

            setState((draft) => ({
                ...draft,
                ratingPercent: ratingPercent,
            }));
        } catch (error) {
            console.error('Error fetching rating percent:', error);
        }
    }

    // Utility functions
    function updateRatingPercentFromList(ratingList, totalRatings) {
        const ratingCounts = {
            5: 0, 4: 0, 3: 0, 2: 0, 1: 0
        };
        
        ratingList.forEach(rating => {
            if (rating.rating >= 1 && rating.rating <= 5) {
                ratingCounts[rating.rating]++;
            }
        });
        
        const updatedRatingPercent = [
            { ratingStar: 5, percent: Math.round((ratingCounts[5] / totalRatings) * 100) },
            { ratingStar: 4, percent: Math.round((ratingCounts[4] / totalRatings) * 100) },
            { ratingStar: 3, percent: Math.round((ratingCounts[3] / totalRatings) * 100) },
            { ratingStar: 2, percent: Math.round((ratingCounts[2] / totalRatings) * 100) },
            { ratingStar: 1, percent: Math.round((ratingCounts[1] / totalRatings) * 100) }
        ];
        
        setState((draft) => ({
            ...draft,
            ratingPercent: updatedRatingPercent,
        }));
    }

    function calculateAverageRating(ratingList) {
        if (!ratingList || ratingList.length === 0) {
            return 0;
        }
        
        const totalRating = ratingList.reduce((sum, rating) => sum + rating.rating, 0);
        const averageRating = totalRating / ratingList.length;
        
        return Math.floor(averageRating);
    }

    function timeDifference(current, previous) {
        const msPerMinute = 60 * 1000;
        const msPerHour = msPerMinute * 60;
        const msPerDay = msPerHour * 24;
        const msPerWeek = msPerDay * 7;
        const msPerMonth = msPerDay * 30;
        const msPerYear = msPerDay * 365;

        const elapsed = current - previous;

        if (elapsed === 0) {
            return translations.ratingTime.justNow
        } else if (elapsed < msPerMinute) {
            return Math.round(elapsed / 1000) + ' ' + translations.ratingTime.secondsAgo;
        } else if (elapsed < msPerHour) {
            return Math.round(elapsed / msPerMinute) + ' ' + translations.ratingTime.minutesAgo;
        } else if (elapsed < msPerDay) {
            return Math.round(elapsed / msPerHour) + ' ' + translations.ratingTime.hoursAgo;
        } else if (elapsed < msPerWeek) {
            return Math.round(elapsed / msPerDay) + ' ' + translations.ratingTime.daysAgo;
        } else if (elapsed < msPerMonth) {
            return Math.round(elapsed / msPerWeek) + ' ' + translations.ratingTime.weeksAgo;
        } else if (elapsed < msPerYear) {
            return Math.round(elapsed / msPerMonth) + ' ' + translations.ratingTime.monthsAgo;
        } else {
            return Math.round(elapsed / msPerYear) + ' ' + translations.ratingTime.yearsAgo;
        }
    }

    const onSaveComment = async () => {
        setIsSending(true);
        if (state.ratingValue === 0) {
            toast.current.show({ 
                severity: 'warn', 
                summary: 'warning', 
                detail: translations.ratingValidateMessage, 
                life: 3000,
                position: 'bottom-right',
                content: (props) => WarnTemplate(props)
            });
            setIsSending(false);
            return;
        }

        const body = {
            rating: state.ratingValue,
            reviewText: state.comment
        }

        if (currentUser) {
            await createRating(body);
        }
        setIsSending(false);
    }

    // Effects
    React.useEffect(() => {
        const loadTranslations = async () => {
            const ratingDropdownTitle = {
                oneStart: await translate("UMBRACO_LANDING.RATING_DROPDOWN_TITLE.ONE_START"),
                twoStart: await translate("UMBRACO_LANDING.RATING_DROPDOWN_TITLE.TWO_START"),
                threeStart: await translate("UMBRACO_LANDING.RATING_DROPDOWN_TITLE.THREE_START"),
                fourStart: await translate("UMBRACO_LANDING.RATING_DROPDOWN_TITLE.FOUR_START"),
                fiveStart: await translate("UMBRACO_LANDING.RATING_DROPDOWN_TITLE.FIVE_START"),
                all: await translate("UMBRACO_LANDING.RATING_DROPDOWN_TITLE.ALL")
            };

            const ratingValidateMessage = await translate("UMBRACO_LANDING.RATING_VALIDATE_MESSAGE.RATING_REQUIRED");

            const ratingTime = {
                justNow: await translate("UMBRACO_LANDING.RATING_TIME.JUST_NOW"),
                secondsAgo: await translate("UMBRACO_LANDING.RATING_TIME.SECOND_AGO"),
                minutesAgo: await translate("UMBRACO_LANDING.RATING_TIME.MINUTES_AGO"),
                hoursAgo: await translate("UMBRACO_LANDING.RATING_TIME.HOURS_AGO"),
                daysAgo: await translate("UMBRACO_LANDING.RATING_TIME.DAYS_AGO"),
                weeksAgo: await translate("UMBRACO_LANDING.RATING_TIME.WEEKS_AGO"),
                monthsAgo: await translate("UMBRACO_LANDING.RATING_TIME.MONTHS_AGO"),
                yearsAgo: await translate("UMBRACO_LANDING.RATING_TIME.YEARS_AGO"),
            }

            const ratingPaginatorButton = {
                previous: await translate("UMBRACO_LANDING.RATING_PAGINATOR_BUTTON.PREVIOUS"),
                next: await translate("UMBRACO_LANDING.RATING_PAGINATOR_BUTTON.NEXT")
            };

            const ratingHeaderContent = {
                header: await translate("UMBRACO_LANDING.RATING_HEADER_CONTENT.HEADER"),
                headerContent: await translate("UMBRACO_LANDING.RATING_HEADER_CONTENT.CONTENT"),
                headerRatingContent: await translate("UMBRACO_LANDING.RATING_HEADER_CONTENT.RATING_CONTENT"),
                headerRatingContentPlaceholder: await translate("UMBRACO_LANDING.RATING_HEADER_CONTENT.PLACEHODLER"),
            };

            const ratingContent = {
                header: await translate("UMBRACO_LANDING.RATING_CONTENT.HEADER"),
                dropdownTitle: await translate("UMBRACO_LANDING.RATING_CONTENT.DROPDOWN_TITLE"),
                star: await translate("UMBRACO_LANDING.RATING_CONTENT.STAR")
            }

            const noReviewMessage = await translate("UMBRACO_LANDING.RATING_CONTENT.NO_REVIEW_MESSAGE");
            const btnSend = await translate("UMBRACO_LANDING.RATING_BUTTON.SEND");

            setTranslations((draft) => ({
                ratingDropdownTitle: ratingDropdownTitle,
                ratingValidateMessage: ratingValidateMessage,
                ratingTime: ratingTime,
                ratingPaginatorButton: ratingPaginatorButton,
                ratingHeaderContent: ratingHeaderContent,
                ratingContent: ratingContent,
                noReviewMessage: noReviewMessage,
                btnSend: btnSend
            }))
        }
        loadTranslations()
    }, [props]);

    React.useEffect(() => {
        if (!isAddingRating) {
            fetchRatingPercent();
            fetchRatingDetails();
        }
    }, [page, rows, state.selectedRatingStar, isAddingRating]);

    React.useEffect(() => {
        if (ratingValueDropdown && ratingValueDropdown.length > 0) {
            setState((draft) => ({
                ...draft,
                selectedRatingStar: ratingValueDropdown[0].value,
            }));
        }
        canUserRatingForTrainerAsync()
    }, []);

    // Computed values
    const ratingValueDropdown = [
        { value: 0, title: translations.ratingDropdownTitle.all },
        { value: 5, title: translations.ratingDropdownTitle.fiveStart },
        { value: 4, title: translations.ratingDropdownTitle.fourStart },
        { value: 3, title: translations.ratingDropdownTitle.threeStart },
        { value: 2, title: translations.ratingDropdownTitle.twoStart },
        { value: 1, title: translations.ratingDropdownTitle.oneStart },
    ];

    const paginatorTemplate = {
        layout: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        FirstPageLink: (options) => {
            return (
                <button type="button" className={options.className} onClick={options.onClick} disabled={options.disabled}>
                <i className="pi pi-angle-double-left"></i>
                </button>
            );
        },
        PrevPageLink: (options) => {
            return (
                <button type="button" className={options.className} onClick={options.onClick} disabled={options.disabled}>
                <i className="pi pi-angle-left"></i>
                </button>
            );
        },
        PageLinks: (options) => {
            return (
                <button type="button" className={options.className} onClick={options.onClick}>
                {options.page + 1}
                </button>
            );
        },
        NextPageLink: (options) => {
            return (
                <button type="button" className={options.className} onClick={options.onClick} disabled={options.disabled}>
                <i className="pi pi-angle-right"></i>
                </button>
            );
        },
        LastPageLink: (options) => {
            return (
                <button type="button" className={options.className} onClick={options.onClick} disabled={options.disabled}>
                <i className="pi pi-angle-double-right"></i>
                </button>
            );
        },
    };


    return (
        <div className="flex flex-column p-3 tutor-rating-container">
            <Toast ref={toast} position="bottom-right" className="keduall-toast"/>
            <div className="rating-overall-container flex gap-4 mb-3">
                <div className="rating-details flex flex-column flex-1 gap-3">
                    {state.ratingPercent.map((detail, index) => (
                        <div className="flex items-center gap-8 w-full" key={`rating-details-${index}`}>
                            <div className="flex items-center gap-2">
                                <Rating className="keduall-rate rate-medium" value={detail.ratingStar} readOnly cancel={false} />
                                <div className="keduall-typography text-16-normal-400 white-space-nowrap text-secondary-txt">
                                    {detail.ratingStar} {translations.ratingContent.star}
                                </div>
                            </div>
                            <div className="flex align-items-center gap-2 flex-1">
                                <div className="w-full">
                                    <ProgressBar
                                        className="keduall-progress rating-progress-bar w-full"
                                        value={Math.min(100, Math.max(0, detail.percent))}
                                        showValue={false}
                                    />
                                </div>
                                <p className="keduall-typography text-16-normal-400 text-right w-3rem">
                                    {Math.min(100, Math.max(0, detail.percent))}%
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Rating Form */}
            {state.canRating && (
                <div className="rating-content-item keduall-gap gap-20 m-0 flex flex-column md:pb-3">
                    <h6 className="keduall-typography h6-default flex m-0">
                        {translations.ratingHeaderContent.header}
                    </h6>
                    <div className="flex">
                        <div className="rating-item-content keduall-gap gap-20 w-full flex flex-column">
                            <div className="rating-item-header flex flex-column gap-2">
                                <div className="rating-item-header-text flex">
                                    <p className="text-default text-sm">{translations.ratingHeaderContent.headerContent}</p>
                                </div>
                                <Rating 
                                    className="keduall-rate rate-medium" 
                                    value={state.ratingValue} 
                                    onChange={onRatingValueChange} 
                                    cancel={false} 
                                />
                            </div>
                            <div className="text-default text-sm">
                                {translations.ratingHeaderContent.headerRatingContent}
                            </div>
                            <div className="flex flex-column gap-3">
                                <InputTextarea 
                                    rows={5} 
                                    value={state.comment} 
                                    onChange={onCommentChange} 
                                    className="keduall-form textarea-default w-full p-3" 
                                    placeholder={translations.ratingHeaderContent.headerRatingContentPlaceholder}
                                    maxLength={500} 
                                    autoResize 
                                />
                                <div className="text-end text-sm">
                                    {state.comment.length}/500
                                </div>
                                <LoadingButton
                                    label={translations.btnSend}
                                    className="keduall-button button-normal-default"
                                    onClick={onSaveComment}
                                    loading={isSending}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Rating List */}
            <div className="keduall-form dropdown-input dropdown-content-container">
                <div className="flex rating-content-header gap-2 align-items-center justify-content-between">
                    <div className="keduall-typography heading-h6-semibold-600">
                        {totalRecord} {translations.ratingContent.header}
                    </div>
                    <div className="keduall-form dropdown-input flex gap-2">
                        <Dropdown
                            className="keduall-form dropdown-input text-default-txt"
                            value={state.selectedRatingStar}
                            options={ratingValueDropdown}
                            optionLabel="title"
                            onChange={handleRatingStarChange}
                            appendTo={document.body}
                        />
                    </div>
                </div>
                
                <div className="rating-content">
                    {state.ratingList && state.ratingList.length > 0 ? (
                        <div>
                            {state.ratingList.map((rating, index, array) => (
                                <div key={`rating-${rating.id || index}`}>
                                    <div className="rating-content-item">
                                        <div className="flex">
                                            <Avatar
                                                className="learner-avatar border-circle mr-3"
                                                shape="circle"
                                                image={modifyMediaUrl(rating.profilePicture || props.defaultProfilePicture)}
                                            />
                                            <div className="rating-item-content flex flex-column gap-075">
                                                <div className="rating-item-header flex flex-column gap-2">
                                                    <div className="rating-item-header-text flex">
                                                        <p className="keduall-typography text-16-medium-500">
                                                            {rating.userDisplayName}
                                                        </p>
                                                        <p className="keduall-typography text-12-normal-400 text-secondary-txt">
                                                            &middot;
                                                        </p>
                                                        <p className="keduall-typography text-14-normal-400 text-secondary-txt">
                                                            {timeDifference(new Date(), convertUTCtoLocalTime(rating.createdTime))}
                                                        </p>
                                                    </div>
                                                    <Rating 
                                                        className="keduall-rate rate-small" 
                                                        value={rating.rating} 
                                                        readOnly 
                                                        cancel={false} 
                                                    />
                                                </div>
                                                <div className="rating-item-text trainer-rating-content">
                                                    <p 
                                                        className="keduall-typography text-16-normal-400 text-secondary-txt"
                                                        dangerouslySetInnerHTML={{ __html: rating.reviewText }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {index !== array.length - 1 && <Divider className="m-0" />}
                                </div>
                            ))}
                            { totalRecord > rows && (
                                <Paginator
                                    className="keduall-paginator"
                                    template={paginatorTemplate}
                                    first={first}
                                    rows={rows}
                                    totalRecords={totalRecord}
                                    onPageChange={onPageChange}
                                    pageLinkSize={totalPages}
                                />
                            )}
                        </div>
                    ) : (
                        <div className="no-review-message">
                            <p className="keduall-typography text-16-normal-400 text-secondary-txt">
                                {translations.noReviewMessage}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}